/**
 * TRAJ-8.27: registre des requêtes en vol, partagé par les stores Pinia.
 *
 * Le cache TTL ne protège que les appels séquentiels : deux composants qui
 * demandent la même donnée au même instant sur un cache froid déclenchaient
 * deux requêtes identiques. Ici le second appelant reçoit la promesse du premier.
 *
 * Le registre vit volontairement HORS du state Pinia : une promesse dans le
 * state serait rendue réactive sans aucun bénéfice.
 *
 * Changement de société / déconnexion : vider la Map n'annule pas une requête
 * déjà partie, sa réponse arrive quand même. Le compteur de génération permet
 * au fetcher de reconnaître une réponse périmée et de ne PAS l'écrire dans le
 * state, sinon les données de la société précédente repeupleraient le store.
 */
export function createInFlightRegistry() {
  const inFlight = new Map()
  let generation = 0

  return {
    /**
     * Exécute `fn` une seule fois par clé tant que sa promesse est en vol.
     *
     * @param {string} key - identifiant de la ressource demandée
     * @param {(isStale: () => boolean) => Promise} fn - effectue la requête ;
     *   reçoit `isStale()`, à consulter APRÈS chaque `await` : s'il renvoie
     *   true, `reset()` a été appelé entre-temps et la réponse ne doit pas
     *   être écrite dans le state.
     * @returns {Promise}
     */
    dedupe(key, fn) {
      const existing = inFlight.get(key)
      if (existing) {
        return existing
      }

      const startedAt = generation
      const isStale = () => startedAt !== generation

      // Le .finally() est non négociable : sans lui, une requête en échec laisserait
      // une promesse rejetée dans le registre et le store ne retenterait jamais.
      // On ne retire que SA propre entrée : après un reset(), une nouvelle requête
      // de même clé a pu prendre la place et doit rester dédupliquée.
      const promise = fn(isStale).finally(() => {
        if (inFlight.get(key) === promise) {
          inFlight.delete(key)
        }
      })
      inFlight.set(key, promise)
      return promise
    },

    /**
     * Oublie les requêtes en vol et marque leurs réponses comme périmées.
     * À appeler au changement de société et à la déconnexion.
     */
    reset() {
      generation += 1
      inFlight.clear()
    }
  }
}
